import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Loader2, Package, Truck, ArrowRight, ArrowLeft, CheckCircle, MapPin, User, Phone, Camera, Shield, CreditCard, DollarSign, Info } from 'lucide-react';
import { trackOTPSent } from '../components/Analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { safeFetchCompat as fetch } from '../utils/safeFetch';

const BookingPage = () => {
  // NEW FLOW: 0: choose type, 1: your details, 2: OTP, 3: service select, 4: address, 5: customer details, 6: photo, 7: payment
  const [step, setStep] = useState(0);
  const [bookingType, setBookingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    otp: '',
    address: '',
    customerName: '',
    customerPhone: '',
    notes: '',
    serviceType: 'standard'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [pricing, setPricing] = useState([]);
  const [error, setError] = useState('');
  const addressDebounceRef = useRef(null);
  const sessionTokenRef = useRef(crypto.randomUUID());

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/active`);
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTypeSelect = (type) => {
    setBookingType(type);
    setStep(1);
  };

  const handleSendOTP = async () => {
    if (!formData.name || !formData.mobile) {
      setError('Please enter your name and mobile number');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    // Track OTP for follow-up
    trackOTPSent(formData.mobile, bookingType === 'pickup' ? 'collection_booking' : 'delivery_booking', formData.name, formData.email, null);
    
    try {
      const response = await fetch(`${API_URL}/api/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: formData.mobile })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setStep(2);
      } else {
        setError(data.detail || data.message || 'Failed to send verification code');
      }
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  const handleVerifyOTP = async () => {
    setIsVerifying(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone_number: formData.mobile,
          code: formData.otp 
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.valid) {
        // Load pricing after OTP verification
        await fetchPricing();
        setStep(3);
      } else {
        setError(data.message || 'Invalid verification code. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    }
    
    setIsVerifying(false);
  };

  const handleAddressInput = (value) => {
    handleChange('address', value);
    
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }
    
    if (value.length > 3) {
      setIsLoadingAddresses(true);
      
      addressDebounceRef.current = setTimeout(async () => {
        try {
          const response = await fetch(
            `${API_URL}/api/places/autocomplete?input=${encodeURIComponent(value)}&sessiontoken=${sessionTokenRef.current}`
          );
          
          if (response.ok) {
            const data = await response.json();
            setAddressSuggestions(data.predictions || []);
            setShowSuggestions(data.predictions?.length > 0);
          }
        } catch (err) {
          console.error('Address lookup error:', err);
        }
        setIsLoadingAddresses(false);
      }, 300);
    } else {
      setShowSuggestions(false);
      setIsLoadingAddresses(false);
    }
  };

  const selectAddress = (suggestion) => {
    handleChange('address', suggestion.description);
    setShowSuggestions(false);
    sessionTokenRef.current = crypto.randomUUID();
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        const base64 = reader.result.split(',')[1];
        setPhotoData(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const createBookingAndPay = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const bookingResponse = await fetch(`${API_URL}/api/bookings/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_type: bookingType,
          service_type: formData.serviceType,
          client_name: formData.name,
          client_mobile: formData.mobile,
          client_email: formData.email || null,
          address: formData.address,
          customer_name: formData.customerName,
          customer_phone: formData.customerPhone,
          photo_data: photoData,
          notes: formData.notes
        })
      });

      if (!bookingResponse.ok) {
        throw new Error('Failed to create booking');
      }

      const booking = await bookingResponse.json();

      const checkoutResponse = await fetch(`${API_URL}/api/payments/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: booking.id,
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        throw new Error('Failed to create payment session');
      }

      const checkout = await checkoutResponse.json();

      if (checkout.url) {
        window.location.href = checkout.url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const getSelectedPricing = () => {
    return pricing.find(p => p.service_type === formData.serviceType);
  };

  // Step labels for progress
  const steps = ['Type', 'Details', 'Verify', 'Level', 'Address', 'Customer', bookingType === 'pickup' ? 'Photo' : null, 'Pay'].filter(Boolean);

  return (
    <main className="bg-[#F8FAFC] min-h-screen">
      {/* Header */}
      <section className="bg-white border-b border-[#E2E8F0] py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111]">
            {step === 0 ? 'What would you like to book?' : 
             bookingType === 'pickup' ? 'Book Your Packaging Collection' : 'Book a Delivery'}
          </h1>
          {step > 0 && (
            <div className="mt-4 flex justify-center gap-2">
              {steps.map((s, idx) => (
                <div 
                  key={s} 
                  className={`w-8 h-1 rounded-full transition-colors ${
                    idx < step ? 'bg-[#0D9488]' : 'bg-[#E2E8F0]'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Step 0: Choose Booking Type */}
          {step === 0 && (
            <div className="space-y-4 animate-fade-in">
              <Card 
                className="border-[#E2E8F0] cursor-pointer card-hover hover:border-[#0D9488] transition-colors"
                onClick={() => handleTypeSelect('delivery')}
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#F0FDFA] flex items-center justify-center">
                    <Truck className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#111111]">Book a Delivery</h3>
                    <p className="text-sm text-[#64748B]">Schedule equipment delivery to your job site</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                </CardContent>
              </Card>

              <Card 
                className="border-[#E2E8F0] cursor-pointer card-hover hover:border-[#0D9488] transition-colors"
                onClick={() => handleTypeSelect('pickup')}
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#F0FDFA] flex items-center justify-center">
                    <Package className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#111111]">Book Packaging Collection</h3>
                    <p className="text-sm text-[#64748B]">We'll collect install packaging from your customer's site</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                </CardContent>
              </Card>

              <p className="text-center text-sm text-[#64748B] mt-6">
                Metro Melbourne service area only
              </p>
            </div>
          )}

          {/* Step 1: Your Details */}
          {step === 1 && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <User className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#111111]">Your Details</h2>
                  <p className="text-sm text-[#64748B] mt-1">We'll verify your number before showing pricing</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Your Name *</label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className="border-[#E2E8F0] focus:border-[#0D9488] h-12"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Mobile Number *</label>
                    <Input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      placeholder="04XX XXX XXX"
                      className="border-[#E2E8F0] focus:border-[#0D9488] h-12"
                      data-testid="client-mobile-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Email <span className="text-[#94A3B8] font-normal">(for confirmation)</span></label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="your@email.com"
                      className="border-[#E2E8F0] focus:border-[#0D9488] h-12"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={handleSendOTP}
                    disabled={!formData.name || !formData.mobile || isSubmitting}
                    data-testid="send-otp-btn"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                    ) : (
                      <>Verify My Number <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#111111]">Verify Your Number</h2>
                  <p className="text-sm text-[#64748B] mt-1">
                    Code sent to <strong>{formData.mobile}</strong>
                  </p>
                </div>

                <div className="space-y-4">
                  <Input
                    type="text"
                    value={formData.otp}
                    onChange={(e) => handleChange('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="border-[#E2E8F0] focus:border-[#0D9488] h-14 text-center text-2xl tracking-widest"
                    maxLength={6}
                    data-testid="otp-input"
                  />
                  <p className="text-center text-sm text-[#64748B]">
                    Didn't receive it? <button onClick={handleSendOTP} disabled={isSubmitting} className="text-[#0D9488] hover:underline">Resend</button>
                  </p>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={handleVerifyOTP}
                    disabled={formData.otp.length < 4 || isVerifying}
                    data-testid="verify-otp-btn"
                  >
                    {isVerifying ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying...</>
                    ) : (
                      <>Verify <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Service Selection (NOW AFTER OTP) */}
          {step === 3 && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#111111]">Select Service Level</h2>
                  <p className="text-sm text-[#64748B] mt-1">Choose the service that best fits your needs</p>
                </div>

                <div className="space-y-3">
                  {pricing.length === 0 ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-[#0D9488] mx-auto" />
                      <p className="text-sm text-[#64748B] mt-2">Loading pricing...</p>
                    </div>
                  ) : (
                    pricing.map((p) => (
                      <button
                        key={p.service_type}
                        onClick={() => handleChange('serviceType', p.service_type)}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                          formData.serviceType === p.service_type
                            ? 'border-[#0D9488] bg-[#F0FDFA]'
                            : 'border-[#E2E8F0] hover:border-[#94A3B8]'
                        }`}
                        data-testid={`service-${p.service_type}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-[#111111]">{p.name}</h3>
                            <p className="text-sm text-[#64748B] mt-1">{p.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-[#0D9488]">${p.price.toFixed(2)}</p>
                            <p className="text-xs text-[#64748B]">AUD</p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={() => setStep(4)}
                    disabled={!formData.serviceType || pricing.length === 0}
                    data-testid="service-next-btn"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Address */}
          {step === 4 && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#111111]">
                    {bookingType === 'pickup' ? 'Collection Address' : 'Delivery Address'}
                  </h2>
                  <p className="text-sm text-[#64748B] mt-1">
                    {bookingType === 'pickup' 
                      ? "Where should we collect the packaging from?" 
                      : "Where should we deliver to?"}
                  </p>
                </div>

                <div className="space-y-4 relative">
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Address *</label>
                    <div className="relative">
                      <Input
                        type="text"
                        value={formData.address}
                        onChange={(e) => handleAddressInput(e.target.value)}
                        placeholder="Start typing an address..."
                        className="border-[#E2E8F0] focus:border-[#0D9488] h-12"
                        data-testid="address-input"
                      />
                      {isLoadingAddresses && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <Loader2 className="w-4 h-4 animate-spin text-[#64748B]" />
                        </div>
                      )}
                    </div>
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {addressSuggestions.map((suggestion, index) => (
                          <button
                            key={suggestion.place_id || index}
                            className="w-full px-4 py-3 text-left hover:bg-[#F8FAFC] text-sm flex items-center gap-2 border-b border-[#F1F5F9] last:border-0"
                            onClick={() => selectAddress(suggestion)}
                          >
                            <MapPin className="w-4 h-4 text-[#0D9488] flex-shrink-0" />
                            <span className="text-[#111111]">{suggestion.description}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={() => setStep(5)}
                    disabled={!formData.address}
                    data-testid="address-next-btn"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 5: Customer Details (CORRECT TERMINOLOGY) */}
          {step === 5 && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#111111]">Customer Contact Details</h2>
                  <p className="text-sm text-[#64748B] mt-1">
                    We'll coordinate directly with your customer to arrange {bookingType === 'pickup' ? 'collection' : 'delivery'}
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-[#F0F9FF] border border-[#BFDBFE] rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[#1E40AF]">
                      <strong>Customer</strong> = the person at the site. Transa will contact them directly to coordinate timing.
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Customer Name *</label>
                    <Input
                      type="text"
                      value={formData.customerName}
                      onChange={(e) => handleChange('customerName', e.target.value)}
                      placeholder="Customer's full name"
                      className="border-[#E2E8F0] focus:border-[#0D9488] h-12"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Customer Phone *</label>
                    <Input
                      type="tel"
                      value={formData.customerPhone}
                      onChange={(e) => handleChange('customerPhone', e.target.value)}
                      placeholder="Customer's phone number"
                      className="border-[#E2E8F0] focus:border-[#0D9488] h-12"
                      data-testid="customer-phone-input"
                    />
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={() => setStep(bookingType === 'pickup' ? 6 : 7)}
                    disabled={!formData.customerName || !formData.customerPhone}
                    data-testid="customer-next-btn"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 6: Photo Upload (only for pickup) */}
          {step === 6 && bookingType === 'pickup' && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <Camera className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#111111]">Photo of Packaging</h2>
                  <p className="text-sm text-[#64748B] mt-1">
                    Upload a photo of the packaging neatly stacked in a safe location
                  </p>
                </div>

                <div className="space-y-4">
                  {photoPreview ? (
                    <div className="relative">
                      <img 
                        src={photoPreview} 
                        alt="Packaging preview" 
                        className="w-full h-48 object-cover rounded-xl border border-[#E2E8F0]"
                      />
                      <button 
                        onClick={() => { setPhotoPreview(null); setPhotoData(null); }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md hover:bg-[#F8FAFC]"
                      >
                        <span className="text-[#EF4444] text-sm font-medium px-2">Remove</span>
                      </button>
                    </div>
                  ) : (
                    <label className="block">
                      <div className="border-2 border-dashed border-[#E2E8F0] rounded-xl p-8 text-center cursor-pointer hover:border-[#0D9488] transition-colors">
                        <Camera className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
                        <p className="text-[#111111] font-medium">Click to upload photo</p>
                        <p className="text-sm text-[#64748B] mt-1">JPG, PNG up to 10MB</p>
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={handlePhotoUpload}
                      />
                    </label>
                  )}
                  
                  <div className="bg-[#F0FDFA] rounded-xl p-4">
                    <p className="text-sm text-[#0F766E]">
                      <strong>Tip:</strong> Stack boxes neatly in a covered or protected area. 
                      We'll send you a photo of the same location after collection.
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={() => setStep(7)}
                  >
                    {photoPreview ? 'Next' : 'Skip for now'} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 7: Review & Payment */}
          {step === 7 && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-[#111111]">Review & Pay</h2>
                  <p className="text-sm text-[#64748B] mt-1">Confirm your booking details and proceed to payment</p>
                </div>

                <div className="space-y-4">
                  <div className="bg-[#F8FAFC] rounded-xl p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Booking Type</span>
                      <span className="font-medium text-[#111111]">
                        {bookingType === 'pickup' ? 'Packaging Collection' : 'Delivery'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Service Level</span>
                      <span className="font-medium text-[#111111]">{getSelectedPricing()?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Client (You)</span>
                      <span className="font-medium text-[#111111]">{formData.name}</span>
                    </div>
                    <div className="border-t border-[#E2E8F0] pt-3">
                      <span className="text-[#64748B] text-sm">Site Address</span>
                      <p className="font-medium text-[#111111]">{formData.address}</p>
                    </div>
                    <div className="border-t border-[#E2E8F0] pt-3">
                      <span className="text-[#64748B] text-sm">Customer Contact</span>
                      <p className="font-medium text-[#111111]">{formData.customerName}</p>
                      <p className="text-sm text-[#64748B]">{formData.customerPhone}</p>
                    </div>
                    {photoPreview && (
                      <div className="border-t border-[#E2E8F0] pt-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-[#22C55E]" />
                          <span className="text-sm text-[#22C55E]">Photo attached</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-[#0D9488]/5 border border-[#0D9488]/20 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-[#0D9488]" />
                        <span className="font-semibold text-[#111111]">Total Amount</span>
                      </div>
                      <span className="text-2xl font-bold text-[#0D9488]">
                        ${getSelectedPricing()?.price.toFixed(2)} AUD
                      </span>
                    </div>
                  </div>

                  <div className="bg-[#F0FDFA] rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <CreditCard className="w-5 h-5 text-[#0D9488] mt-0.5" />
                      <p className="text-sm text-[#0F766E]">
                        You'll be redirected to our secure payment partner (Stripe) to complete your booking.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={createBookingAndPay}
                    disabled={isSubmitting}
                    data-testid="pay-btn"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay ${getSelectedPricing()?.price.toFixed(2)} AUD
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </section>
    </main>
  );
};

export default BookingPage;
